import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { useState, useRef, useEffect } from 'react';
import searchFill from '@iconify/icons-eva/search-fill';
import { Link as RouterLink } from 'react-router-dom';
import roundSend from '@iconify/icons-ic/round-send';
import heartFill from '@iconify/icons-eva/heart-fill';
import shareFill from '@iconify/icons-eva/share-fill';
import moreVerticalFill from '@iconify/icons-eva/more-vertical-fill';
import messageSquareFill from '@iconify/icons-eva/message-square-fill';
import roundAddPhotoAlternate from '@iconify/icons-ic/round-add-photo-alternate';
// material
import {
  Box,
  Link,
  Card,
  Stack,
  Paper,
  Avatar,
  TextField,
  Typography,
  Button,
  CardHeader,
  IconButton,
  AvatarGroup,
  OutlinedInput,
  InputAdornment,
  FormControlLabel,
  CircularProgress,
  Menu,
  MenuItem,
  TablePagination
} from '@material-ui/core';
import { experimentalStyled as styled } from '@material-ui/core/styles';
import { PictureAsPdf, GridOn, ArrowDropDown, Print } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
// utils
import { format } from 'date-fns';
import { fShortenNumber } from '../../../../utils/formatNumber';
// hooks
import useAuth from '../../../../hooks/useAuth';
//
import MyAvatar from '../../../MyAvatar';
import EmojiPicker from '../../../EmojiPicker';
import { MCheckbox } from '../../../@material-extend';
import Scrollbar from 'src/components/Scrollbar';

// ----------------------------------------------------------------------
const SEARCH_DEBOUNCE_MS = 600;

const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
  width: '50%',
  transition: theme.transitions.create(['box-shadow'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter
  }),
  '&.Mui-focused': { boxShadow: theme.customShadows.z8 },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${theme.palette.grey[500_32]} !important`
  }
}));

const formatJobLogTimestamp = (comment) => {
  const raw = Number(comment?.raw_timestamp);
  if (!raw) {
    return comment?.timestamp ?? '';
  }
  const date = new Date(raw * 1000);
  return format(date, 'dd MMM yyyy, h:mm a');
};

const countryCodeToFlagUrl = (code) => {
  if (!code || String(code).length !== 2) return '';
  // SVG flags — crisp on Windows/Mac/Linux (emoji flags don't render on Windows)
  return `https://flagcdn.com/${String(code).toLowerCase()}.svg`;
};

ProfilePostCard.propTypes = {
  post: PropTypes.object
};


export default function ProfilePostCard({ updates, details, searchable = false, onSearchUpdates }) {
  console.log("data34343434 : ",details);
  const nameofjob=details?.name||'updates';
  const [jobName,setJobName] = useState(nameofjob);
  const safeUpdates = updates || [];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchRequestRef = useRef(0);
  const onSearchUpdatesRef = useRef(onSearchUpdates);

  useEffect(() => {
    onSearchUpdatesRef.current = onSearchUpdates;
  }, [onSearchUpdates]);

  const activeUpdates = searchResults !== null ? searchResults : safeUpdates;
  const paginatedComments = activeUpdates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    setPage(0);
  }, [searchResults, updates]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (!searchable) return undefined;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      const trimmedSearch = searchTerm.trim();

      if (!trimmedSearch) {
        setSearchResults(null);
        setSearchLoading(false);
        return;
      }

      if (typeof onSearchUpdatesRef.current !== 'function') {
        return;
      }

      const requestId = searchRequestRef.current + 1;
      searchRequestRef.current = requestId;
      setSearchLoading(true);

      try {
        const results = await onSearchUpdatesRef.current(trimmedSearch);
        if (searchRequestRef.current === requestId) {
          setSearchResults(Array.isArray(results) ? results : []);
        }
      } catch (error) {
        if (searchRequestRef.current === requestId) {
          setSearchResults([]);
        }
      } finally {
        if (searchRequestRef.current === requestId) {
          setSearchLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, searchable]);

  const ActivityComponent = ({ activity }) => {
    return (
      <Typography
        variant="body1"
        component="div"
        sx={{ '& strong': { fontWeight: 'bold' } }}
        dangerouslySetInnerHTML={{ __html: activity }}
      />
    );
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    let text = tmp.textContent || tmp.innerText || '';
    text = text.replace(/[\u2018\u2019]/g, "'")
               .replace(/[\u201C\u201D]/g, '"')
               .replace(/[\u2013\u2014]/g, '-')
               .replace(/\u00A0/g, " ")
               .replace(/[^\x20-\x7E]/g, '');
    return text.trim();
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleExportMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setAnchorEl(null);
  };

  const getExportData = () => {
    // For Print/Export we must keep showing the full logs (not the searched/filtered subset).
    const data = safeUpdates || [];
    return data.map((item, index) => {
      const  label  = formatJobLogTimestamp(item);
      return {
        'SL No': index + 1,
        'Name': item.name || 'N/A',
        'Activity': stripHtml(item.activity),
        'Date & Time': label || 'N/A'
      };
    });
  };

  const handleExportExcel = () => {
    try {
      const data = getExportData();
      if (data.length === 0) {
        enqueueSnackbar('No data available to export', { variant: 'warning' });
        return;
      }
      const ws = XLSX.utils.json_to_sheet(data);
      
      // Set column widths to prevent text from being cut off visually
      ws['!cols'] = [
        { wch: 8 },   // SL No
        { wch: 25 },  // Name
        { wch: 80 },  // Activity
        { wch: 25 },  // Date & Time
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Updates');
      XLSX.writeFile(wb, `Updates_Export_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
      enqueueSnackbar('Excel exported successfully', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to export Excel', { variant: 'error' });
    }
    handleExportMenuClose();
  };

  const handleExportPDF = () => {
    try {
      const data = getExportData();
      if (data.length === 0) {
        enqueueSnackbar('No data available to export', { variant: 'warning' });
        return;
      }
      const doc = new jsPDF('portrait');
      doc.text(`Job Log Report Of ${jobName}`, 14, 14);
      doc.setFontSize(10);
      doc.text(`Generated on: ${format(new Date(), 'dd MMM yyyy, h:mm a')}`, 14, 20);
      
      const tableColumn = Object.keys(data[0]);
      const tableRows = data.map(item => Object.values(item));

      autoTable(doc, {
        startY: 25,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        styles: { fontSize: 8, overflow: 'linebreak' },
        columnStyles: {
          2: { cellWidth: 90 }
        },
        headStyles: { fillColor: [0, 144, 200] },
      });

      doc.save(`Updates_Export_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`);
      enqueueSnackbar('PDF exported successfully', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to export PDF', { variant: 'error' });
    }
    handleExportMenuClose();
  };

  const handlePrint = () => {
    try {
      const data = getExportData();
      if (data.length === 0) {
        enqueueSnackbar('No data available to print', { variant: 'warning' });
        return;
      }
      
      const headers = Object.keys(data[0]);
      const headerCells = headers.map(h => `<th>${h}</th>`).join('');
      const htmlRows = data.map(row => {
        const cells = Object.values(row).map(val => `<td>${val}</td>`).join('');
        return `<tr>${cells}</tr>`;
      }).join('');
      
      const printContent = `
        <!DOCTYPE html>
        <html>
            <head>
                <meta charset="utf-8" />
                <title>Job Log Report</title>
                <style>
                    body { font-family: sans-serif; padding: 20px; }
                    h2 { text-align: center; margin-bottom: 5px; }
                    .meta { text-align: center; margin-bottom: 20px; font-size: 14px; color: #555; }
                    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                    th, td { border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 14px; }
                    th { background-color: #f4f6f8; font-weight: bold; }
                    tr:nth-child(even) { background-color: #f9f9f9; }
                </style>
            </head>
            <body>
                <h2>Job Log Report Of ${jobName}</h2>
                <div class="meta">Generated on: ${format(new Date(), 'dd MMM yyyy, h:mm a')}</div>
                <table>
                    <thead><tr>${headerCells}</tr></thead>
                    <tbody>${htmlRows}</tbody>
                </table>
            </body>
        </html>
      `;

      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.top = '0';
      printFrame.style.left = '-10000px';
      printFrame.style.width = '100vw';
      printFrame.style.height = '100vh';
      printFrame.style.border = '0';
      document.body.appendChild(printFrame);

      const frameDoc = printFrame.contentWindow?.document;
      if (!frameDoc) {
        document.body.removeChild(printFrame);
        enqueueSnackbar('Failed to open print preview', { variant: 'error' });
        return;
      }

      let hasPrinted = false;
      const runPrint = () => {
        if (hasPrinted) return;
        hasPrinted = true;
        printFrame.contentWindow?.focus();
        printFrame.contentWindow?.print();
        setTimeout(() => {
          if (document.body.contains(printFrame)) {
            document.body.removeChild(printFrame);
          }
        }, 300);
      };

      printFrame.onload = runPrint;
      frameDoc.open();
      frameDoc.write(printContent);
      frameDoc.close();
      setTimeout(runPrint, 250);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to print', { variant: 'error' });
    }
  };

  return (
    <Card variant="outlined" className="sw-request-tab-updates" sx={{ height: '550px', mb: 2, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          mt: 2,
          mb:1 ,
          px: 3,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: searchable ? 'space-between' : 'flex-end',
          gap: 2,
        }}
      >
        {searchable && (
          <SearchStyle
            sx={{ backgroundColor: 'var(--signwise-app-chrome-toolbar)', height: 40 }}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by user name or message..."
            startAdornment={
              <InputAdornment position="start">
                <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            endAdornment={
              searchLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : null
            }
          />
        )}
        <Box sx={{ display: 'flex', flexShrink: 0 }}>
        <Button variant="outlined" onClick={handlePrint} startIcon={<Print />} sx={{ mr: 1 }}>
          Print
        </Button>
        <Button
          variant="contained"
          onClick={handleExportMenuOpen}
          endIcon={<ArrowDropDown />}
        >
          Export
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleExportMenuClose}
        >
          <MenuItem onClick={handleExportExcel}>
            <GridOn sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} /> Excel
          </MenuItem>
          <MenuItem onClick={handleExportPDF}>
            <PictureAsPdf sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} /> PDF
          </MenuItem>
        </Menu>
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        <Scrollbar sx={{ height: '100%' }}>
      <Stack spacing={3} sx={{ p: 3 }}>
        {updates === null || updates === undefined ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '300px' }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <>
            <Stack spacing={1.5}>
              {paginatedComments.map((comment) => (
            <Stack key={comment.id} direction="row" spacing={2}>
              <Avatar alt={comment.name} src={comment.profile_pic} />
              <Paper sx={{ p: 1.5, flexGrow: 1, bgcolor: 'background.neutral' }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>{comment.name}</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    {countryCodeToFlagUrl(comment.country_code) && (
                      <Box
                        component="img"
                        src={countryCodeToFlagUrl(comment.country_code)}
                        alt={comment.country_code}
                        title={comment.country_code}
                        aria-label={`Country ${comment.country_code}`}
                        sx={{ width: 22, height: 16, display: 'inline-block', flexShrink: 0 }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      component="span"
                      sx={{ color: 'primary.main'}}
                    >
                      {formatJobLogTimestamp(comment)}
                    </Typography>
                  </Stack>
                </Stack>
                <Typography
                  variant="body2"
                  className={comment?.is_jobcost == 2 ? 'sw-request-tab-updates--jobcost-alert' : undefined}
                >
                  {/* {comment.activity} */}
                  <ActivityComponent activity={comment.activity} />
                </Typography>
              </Paper>
            </Stack>
          ))}
        </Stack>
            {searchable && searchTerm.trim() && !searchLoading && activeUpdates.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No matching job log found.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Stack>
        </Scrollbar>
      </Box>
      {updates !== null && updates !== undefined && (
        <TablePagination
          component="div"
          count={activeUpdates.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            flexShrink: 0,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        />
      )}
    </Card>
  );
}

ProfilePostCard.propTypes = {
  updates: PropTypes.array,
  details: PropTypes.object,
  searchable: PropTypes.bool,
  onSearchUpdates: PropTypes.func
};
